import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Spinner } from 'src/components/Spinner'
import { ShowDialogProps } from 'src/show-dialog'
import { Box, IconButton, Tooltip, Typography, Stack, Divider, styled, useTheme } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined'
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined'
import { getThemeColor } from 'src/utils'
import { MUIThemeColors } from 'src/mui-theme'

interface Props extends ShowDialogProps {
  fileUrl: string
  altText?: string
  title?: string
  onReplace?: () => string | void
  onDelete?: () => void
}

export const ShowImageDialog: React.FC<Props> = ({ pop, fileUrl, altText, title, onReplace, onDelete }) => {
  const [loading, setLoading] = useState(true)
  const { t } = useTranslation(['common'])
  const theme = useTheme()

  const handleLoad = () => {
    setLoading(false)
  }

  return (
    <DialogContainer>
      <ImageContainer>
        {loading && (
          <LoadingOverlay>
            <Spinner />
          </LoadingOverlay>
        )}

        <PreviewImage src={fileUrl} alt={altText || t('preview-image', { ns: 'common' })} onLoad={handleLoad} />
      </ImageContainer>

      <Divider />
      <FooterBar>
        <DialogTitle variant="h6">{title || t('preview-image', { ns: 'common' })}</DialogTitle>

        <ActionButtons>
          {onReplace && (
            <Tooltip title={t('replace', { ns: 'common' }) as string}>
              <IconButton
                onClick={onReplace}
                sx={{
                  color: getThemeColor(theme, MUIThemeColors.primaryMain),
                  border: `1px solid ${getThemeColor(theme, MUIThemeColors.secondaryMain)}`,
                }}
                aria-label={t('replace', { ns: 'common' })}
                size="small"
              >
                <CloudUploadOutlinedIcon />
              </IconButton>
            </Tooltip>
          )}
          {onDelete && (
            <Tooltip title={t('delete', { ns: 'common' }) as string}>
              <IconButton
                onClick={onDelete}
                sx={{
                  color: getThemeColor(theme, MUIThemeColors.primaryMain),
                  border: `1px solid ${getThemeColor(theme, MUIThemeColors.secondaryMain)}`,
                }}
                aria-label={t('delete', { ns: 'common' })}
                size="small"
              >
                <DeleteOutlineOutlinedIcon />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title={t('close', { ns: 'common' }) as string}>
            <IconButton
              onClick={pop}
              sx={{
                color: getThemeColor(theme, MUIThemeColors.primaryMain),
                border: `1px solid ${getThemeColor(theme, MUIThemeColors.secondaryMain)}`,
              }}
              aria-label={t('close', { ns: 'common' })}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </ActionButtons>
      </FooterBar>
    </DialogContainer>
  )
}

export const DialogContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  width: 'auto',
  height: '60vh',
  aspectRatio: '3/2',
  overflow: 'hidden',
  backgroundColor: getThemeColor(theme, MUIThemeColors.white),
}))

export const ImageContainer = styled(Box)(({ theme }) => ({
  flex: 1,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  overflow: 'hidden',
  position: 'relative',
  backgroundColor: getThemeColor(theme, MUIThemeColors.white),
}))

export const LoadingOverlay = styled(Box)({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 1,
})

export const PreviewImage = styled('img')({
  width: '100%',
  height: '100%',
  objectFit: 'contain',
})

export const FooterBar = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  padding: theme.spacing(1.5),
  backgroundColor: getThemeColor(theme, MUIThemeColors.white),
}))

export const DialogTitle = styled(Typography)({
  flexGrow: 1,
  marginLeft: '20px',
})

export const ActionButtons = styled(Stack)({
  flexDirection: 'row',
  gap: 12,
})
